/**
 * One invoice, in full.
 *
 * The list said what an invoice was for and what it cost; everything else — who
 * it is for, what it was priced from, what happened to it, and what settled it
 * — meant reading the database. This is the page an operator opens when a
 * tenant rings up about a number on a bill.
 *
 * It is also the thing that gets printed. The browser's own print output is
 * made correct by the `doc-*` classes and the `@media print` block in
 * `index.css`; the Download PDF button asks the API for a PDF generated from
 * the same rows. Neither is stored — the operator's rule, and the right one: a
 * saved PDF goes stale the moment the note is corrected.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, History, Receipt } from 'lucide-react';
import { invoiceNumber, type InvoiceDetail } from '@simpleacs/shared';
import { Card, ErrorNotice, Pill, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { INVOICE_TONE, METHOD_LABEL, monthDate, periodLabel } from './common';
import {
  BackLink,
  BrandHeader,
  DocumentActions,
  HistoryList,
  LineTable,
  MetaGrid,
  Parties,
} from './parts';

export function InvoiceDetailPage() {
  // The document's NUMBER — `/billing/invoices/1834`. Its opaque id still
  // resolves server-side, so a bookmark from before the numbers existed opens.
  const { ref = '' } = useParams();
  const query = useQuery({
    queryKey: ['billing', 'invoice', ref],
    queryFn: () => api.get<InvoiceDetail>(`/billing/invoices/${encodeURIComponent(ref)}`),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;
  const invoice = query.data;
  if (!invoice) return null;

  return (
    <>
      <BackLink to="/billing">Back to billing</BackLink>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-xl font-semibold text-ink">{invoiceNumber(invoice.number)}</h1>
            <Pill state tone={INVOICE_TONE[invoice.status] ?? 'neutral'}>{invoice.status}</Pill>
          </div>
          <p className="mt-1 text-sm text-muted">
            {invoice.orgName ?? 'Unknown organization'} · raised {monthDate(invoice.createdAt)}
            {invoice.createdBy === 'billing-run' ? ' by the monthly run' : ''}
          </p>
        </div>
        <DocumentActions pdfHref={`/api/billing/invoices/${encodeURIComponent(invoice.id)}/pdf`} />
      </div>

      <div className="space-y-5">
        <section className="doc-sheet card overflow-hidden">
          <BrandHeader brand={invoice.brand} title="INVOICE" reference={invoiceNumber(invoice.number)} />
          <Parties
            brand={invoice.brand}
            toTitle="Billed to"
            toName={invoice.orgName ?? 'Unknown organization'}
            toSub={invoice.orgSlug}
          />
          <MetaGrid
            items={[
              {
                label: 'Invoice number',
                value: <span className="font-mono">{invoiceNumber(invoice.number)}</span>,
              },
              { label: 'Issued', value: monthDate(invoice.createdAt) },
              { label: 'Due', value: invoice.dueAt ? monthDate(invoice.dueAt) : '—' },
              {
                label: 'Service period',
                value: periodLabel(invoice.periodStart, invoice.periodEnd),
              },
              {
                label: 'Billable OLTs',
                value: (
                  <span title="What this invoice was raised against, recorded when it was raised — not a count of the chassis the organization has today.">
                    {invoice.oltCount}
                  </span>
                ),
              },
              { label: 'Paid', value: invoice.paidAt ? monthDate(invoice.paidAt) : '—' },
            ]}
          />
          <LineTable
            lines={invoice.lines}
            currency={invoice.currency}
            totalMinor={invoice.amountMinor}
            fallbackLabel="Platform service"
            accentColor={invoice.brand.accentColor}
          />
          {invoice.note && (
            <div className="doc-keep border-t border-line px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-faint">Note</p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-muted">{invoice.note}</p>
            </div>
          )}
          {invoice.instructions.length > 0 && (
            <div className="doc-keep border-t border-line px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-faint">
                How to pay
              </p>
              <ul className="mt-1.5 space-y-1">
                {invoice.instructions.map((instruction) => (
                  <li key={instruction.method} className="text-sm text-muted">
                    <span className="font-medium text-ink">{instruction.label}</span> —{' '}
                    {instruction.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {invoice.brand.footerNote && (
            <p className="border-t border-line px-5 py-2.5 text-center text-xs text-faint">
              {invoice.brand.footerNote}
            </p>
          )}
        </section>

        {/* The payment that settled it. `invoices.payment_id` is written by
            settlement, so this is the actual money — not an inference from
            dates lining up. */}
        {invoice.payment && (
          <Card
            title={
              <span className="flex items-center gap-2">
                <Receipt className="size-4 text-muted" />
                Settled by
              </span>
            }
          >
            <Link
              to={`/billing/payments/${encodeURIComponent(invoice.payment.id)}`}
              className="doc-keep flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 hover:bg-surface-2/70"
            >
              <span className="text-sm text-muted">
                <b className="text-ink">
                  {formatMoney(invoice.payment.amountMinor, invoice.payment.currency)}
                </b>{' '}
                · {METHOD_LABEL[invoice.payment.method]} · {invoice.payment.months} month
                {invoice.payment.months === 1 ? '' : 's'} ·{' '}
                {periodLabel(invoice.payment.periodStart, invoice.payment.periodEnd)}
              </span>
              <span className="print:hidden text-xs text-primary">Open the payment</span>
            </Link>
          </Card>
        )}

        <Card
          title={
            <span className="flex items-center gap-2">
              <History className="size-4 text-muted" />
              History
            </span>
          }
        >
          <HistoryList history={invoice.history} />
        </Card>

        <p className="print:hidden flex items-center gap-2 text-xs text-faint">
          <FileText className="size-3.5" />
          No PDF is stored. Print and Download both render this invoice from the database as it
          stands right now.
        </p>
      </div>
    </>
  );
}
